<template>
  <div class="container-fluid">
    <div class="loading" v-if="isLoading">Loading&hellip;</div>
    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Existencias en stock</h1>
          <p class="view-header__subtitle">Consultá y ajustá el stock de tus productos por depósito.</p>
        </div>
        <div class="view-header__actions">
          <button type="button" class="btn view-header__btn view-header__btn--primary" @click="exportCSV()">
            <i class="bi bi-download"></i>
            Exportar CSV
          </button>
        </div>
      </div>
    </div>

    <transition name="fade">
      <div v-if="showAlert" class="alert alert-danger alert-dismissible fade show d-flex" role="alert">
        <i class="bi bi-info-circle mr-2"></i>
        <strong>Ayuda:</strong>
        <p class="ml-1 mb-0">
          Ingrese el total que contó en ese depósito y presione Enter para confirmar. El sistema
          calcula solo la diferencia y registra el movimiento correspondiente.
        </p>
        <button type="button" class="button-close ml-auto" data-bs-dismiss="alert" aria-label="Close" @click="closeAlert">
          <i class="bi-x"></i>
        </button>
      </div>
    </transition>

    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__section-title">
          <i class="bi bi-funnel"></i>
          Filtros
        </div>
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <q-select v-model="filters.brand_id" :options="brandFilterOptions" placeholder="- Marca -" />
          </div>
          <div class="filters-panel__field">
            <q-select v-model="filters.category_id" :options="categoryFilterOptions" placeholder="- Categoría -" />
          </div>
          <div class="filters-panel__field">
            <q-select v-model="filters.provider_id" :options="providerFilterOptions" placeholder="- Proveedor -" />
          </div>
          <div class="filters-panel__field">
            <q-select
              v-model="filters.active"
              :options="[{ value: 0, label: 'Todos los productos' }, { value: 1, label: 'Productos activos' }, { value: 2, label: 'Productos inactivos' }]"
            />
          </div>
          <div class="filters-panel__field">
            <label class="toggle-switch">
              <input v-model="filters.negative_stock" type="checkbox">
              <span class="toggle-switch__slider"></span>
              <span class="toggle-switch__text">Solo con stock negativo</span>
            </label>
          </div>
        </div>
      </div>

      <div class="filters-panel__section">
        <div class="filters-panel__section-title">
          <i class="bi bi-search"></i>
          Buscadores
        </div>
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <input v-model="filters.qcode" class="form-control" placeholder="Buscar codigo interno...">
          </div>
          <div class="filters-panel__field">
            <input v-model="filters.qbarcode" class="form-control" placeholder="Buscar codigo de barra...">
          </div>
          <div class="filters-panel__field">
            <input v-model="filters.qmanufacturercode" class="form-control" placeholder="Buscar codigo de fabrica...">
          </div>
          <div class="filters-panel__field">
            <input v-model="filters.qname" class="form-control" placeholder="Buscar nombre...">
          </div>
          <div class="filters-panel__actions">
            <button type="button" class="filters-panel__btn filters-panel__btn--outline" @click="clearFilters">
              <i class="bi bi-eraser"></i>
              Limpiar todo
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- TABLA CON EDITOR -->
    <div class="data-table">
      <div class="table-responsive">
        <table class="table mb-0">
          <thead>
            <tr>
              <th>Producto <a href="#" v-on:click="sortList('name')" class="texto-negro">▼</a></th>
              <th v-for="warehouse in warehouses" :key="warehouse.id">{{ warehouse.name }}</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, index) in products" :key="index">
              <td>{{ product.name }}</td>
              <td v-for="warehouse in warehouses" :key="warehouse.id" class="stock-cell" :class="{ 'stock-cell--negative': isNegative(product.stock[warehouse.id]) }">
                <div class="stock-cell__row">
                  <span class="stock-cell__current">{{ product.stock[warehouse.id] }}</span>
                  <input
                    :value="countInputs[cellKey(product, warehouse)]"
                    @input="setCount(product, warehouse, $event.target.value)"
                    @keyup.enter="saveStock(product, warehouse)"
                    @keyup.esc="resetCount(product, warehouse)"
                    placeholder="conteo"
                    class="stock-cell__input"
                    :class="{ 'stock-cell__input--pending': isPending(product, warehouse) }">
                </div>
                <div v-if="isPending(product, warehouse)" class="stock-cell__hint">
                  {{ countHint(product, warehouse) }} — Enter para confirmar, Esc para descartar
                </div>
              </td>
              <td class="total-cell" :class="{ 'stock-cell--negative': isNegative(totalStock(product)) }">{{ totalStock(product) }}</td>
            </tr>
            <tr v-if="!isLoading && products.length === 0">
              <td :colspan="warehouses.length + 2" class="empty-state">No se encontraron productos con los filtros aplicados.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- PAGINACION -->
      <div class="data-table__footer">
        <div class="data-table__info">
          Mostrando {{ (filters.page - 1) * filters.itemsPerPage + 1 }} a {{ Math.min(filters.page * filters.itemsPerPage, products.length) }} de {{ totalItems }} registros
        </div>
        <ul class="data-table__pagination">
          <li class="page-item" v-bind:class="[{'disabled': filters.page==1}]">
            <a class="page-link" href="#" v-on:click="prevPage()">Anterior</a>
          </li>
          <li v-if="(filters.page - (pageInterval/2))>0" class="page-item">
            <a class="page-link" href="#" v-on:click="prevPage()">...</a>
          </li>
          <template v-for="index in totalPages">
            <li :key="index" v-if="inIntervalPage(index)" class="page-item" v-bind:class="[{'active': filters.page==index}]">
              <a class="page-link" href="#" v-on:click="changePage(index)">{{index}}</a>
            </li>
          </template>
          <li v-if="totalPages>(filters.page + (pageInterval/2))" class="page-item">
            <a class="page-link" href="#" v-on:click="nextPage()">...</a>
          </li>
          <li class="page-item" v-bind:class="[{'disabled': filters.page>=totalPages}]">
            <a class="page-link" href="#" v-on:click="nextPage()">Siguiente</a>
          </li>
        </ul>
        <label class="data-table__page-size">
          Mostrar
          <q-select
            v-model.number="filters.itemsPerPage"
            :options="[{ value: 15, label: '15' }, { value: 25, label: '25' }, { value: 50, label: '50' }]"
          />
          registros
        </label>
      </div>
    </div>
  </div>
</template>

  <script>
  import QSelect from '@/components/shared/QSelect.vue';

  export default {
    components: { QSelect },
    data: function() {
      return {
        filters:{
          qname: '',
          qcode: '',
          qbarcode: '',
          qmanufacturercode: '',
          brand_id: null,
          category_id: null,
          provider_id: null,
          page:1,
          itemsPerPage:15,
          active:1,
          negative_stock: false,
          sorting:{
            col:'',
            order:'desc'
          }
        },
        isLoading: false,
        suppressReload: false,
        warehouses: [],
        brands: [],
        categories: [],
        providers: [],
        products: [],
        totalPages: 1,
        pageInterval: 16,
        totalItems: 0,
        showAlert: true,
        // Total contado tipeado por celda, por "productId-warehouseId".
        // Separado de product.stock para no pisar el total mostrado mientras se edita.
        countInputs: {},
        reason: "Movimiento manual desde Existencias en stock",
      }
    },
    mounted() {
      this.getWarehouses();
      this.getBrands();
      this.getCategories();
      this.getProviders();
      this.getStock();
    },
    methods: {
      getWarehouses(){
        axios.get('/api/warehouses/list')
        .then(response => {
          if(response.status == 200){
            this.warehouses = response.data.data;
          }
        })
      },
      getBrands(){
        axios.get('/api/brands/getBrands')
        .then(response => {
          if(response.status == 200){
            this.brands = response.data.data;
          }
        })
      },
      getCategories(){
        axios.get('/api/categories/getCategories')
        .then(response => {
          if(response.status == 200){
            this.categories = response.data.data;
          }
        })
      },
      getProviders(){
        axios.get('/api/providers/getProviders')
        .then(response => {
          if(response.status == 200){
            this.providers = response.data.data;
          }
        })
      },
      // Resetea filtros y buscadores a sus valores por defecto. El flag suppressReload
      // evita que los watchers disparen una peticion por cada campo; recargamos una sola vez.
      clearFilters(){
        clearTimeout(this.timeoutId);
        this.suppressReload = true;
        this.filters.qname = '';
        this.filters.qcode = '';
        this.filters.qbarcode = '';
        this.filters.qmanufacturercode = '';
        this.filters.brand_id = null;
        this.filters.category_id = null;
        this.filters.provider_id = null;
        this.filters.active = 1;
        this.filters.negative_stock = false;
        this.filters.page = 1;
        this.$nextTick(() => {
          this.suppressReload = false;
          this.getStock();
        });
      },
      getStockWithDelay(){
        clearTimeout(this.timeoutId);
          this.timeoutId = setTimeout(()=>this.getStock(), 1500);
      },
      getStock(){
        let vm = this;
        vm.isLoading = true;
        return axios.post('/api/existences/list', vm.filters)
        .then(response => {
          if(response.status == 200){
            vm.products = response.data.data.products;
            vm.totalPages = response.data.data.totalPages;
            vm.totalItems = response.data.data.totalItems;
          }
        })
        .finally(() => { vm.isLoading = false; })
      },
      closeAlert() {
        this.showAlert = false;
      },
      refreshTable() {
        var vm = this;
        if (vm.$refs.table !== undefined) vm.$refs.table.reload();
      },
      exportCSV(){
        this.$http.post(process.env.VUE_APP_BASE_URL+"/api/existences/exportCSV")
        .then(() => {
        });
      },
      sortList(col){
        if(this.filters.sorting.col == col){
            if(this.filters.sorting.order=='asc'){
                this.filters.sorting.order='desc';
            }else{
                this.filters.sorting.order='asc';
            }
        }else{
            this.filters.sorting.col=col;
            this.filters.sorting.order='asc';
        }
        this.getStock();
      },
      prevPage(){
        if(this.filters.page>1){
            this.filters.page = this.filters.page-1;
            this.getStock();
        }
      },
      nextPage(){
        if (this.filters.page<this.totalPages){
            this.filters.page = this.filters.page +1;
            this.getStock();
        }
      },
      changePage(data){
        this.filters.page = data;
        this.getStock();
      },
      inIntervalPage(index){
        if(this.totalPages<=this.pageInterval){return true;}

        let limitprev = this.filters.page - (this.pageInterval/2);
        let limitnext = this.filters.page + (this.pageInterval/2);

        if(limitprev<0){
            limitprev=0;
            limitnext=this.pageInterval;
        }

        if(index>limitprev && index<limitnext){return true;}

        return false;
      },
      cellKey(product, warehouse) {
        return `${product.id}-${warehouse.id}`;
      },
      totalStock(product) {
        return this.warehouses.reduce((sum, warehouse) => sum + Number(product.stock[warehouse.id] || 0), 0);
      },
      isNegative(value) {
        return Number(value || 0) < 0;
      },
      setCount(product, warehouse, value) {
        this.$set(this.countInputs, this.cellKey(product, warehouse), value);
      },
      resetCount(product, warehouse) {
        this.$delete(this.countInputs, this.cellKey(product, warehouse));
      },
      // Devuelve el total contado tipeado en la celda, o null si no es un numero
      // valido o es negativo (no se puede contar menos de 0 unidades).
      parsedCount(product, warehouse) {
        const raw = String(this.countInputs[this.cellKey(product, warehouse)] ?? '').trim().replace(',', '.');
        if (raw === '') return null;
        const count = Number(raw);
        return Number.isFinite(count) && count >= 0 ? count : null;
      },
      isPending(product, warehouse) {
        const raw = this.countInputs[this.cellKey(product, warehouse)];
        return raw !== undefined && String(raw).trim() !== '';
      },
      countHint(product, warehouse) {
        const count = this.parsedCount(product, warehouse);
        if (count === null) return 'Ingrese un conteo válido (0 o mayor)';
        const loaded = Number(product.stock[warehouse.id] || 0);
        const delta = Number((count - loaded).toFixed(4));
        if (delta === 0) return 'Sin cambios respecto al stock actual';
        return delta > 0 ? `Registrará una entrada de ${delta} unidades` : `Registrará una salida de ${Math.abs(delta)} unidades`;
      },
      // Relee el stock del backend para ese producto y deposito (endpoint liviano,
      // sin precios/categorias/variantes). Lanza si la respuesta no sirve: guardar
      // contra un total viejo es lo que descuadra el ajuste.
      async currentStock(product, warehouse) {
        const response = await this.$http.get(`/api/products/view/${product.id}/stock`);
        const data = (response.data && response.data.data) || {};
        const breakdown = data.stock_by_warehouse;
        if (!Array.isArray(breakdown)) {
          throw new Error('Respuesta inesperada de products/view/stock');
        }
        const entry = breakdown.find(item => item.id === warehouse.id);
        return Number(entry ? entry.stock : 0);
      },
      pushError(message) {
        this.$store.commit('errors/pushErrors', { stock: [message] });
      },
      async saveStock(product, warehouse){
        const count = this.parsedCount(product, warehouse);
        if (count === null) {
          this.pushError('Ingrese un conteo válido (0 o mayor).');
          return;
        }

        // Releemos el stock actual (no el de la pantalla) porque pudo moverse
        // por ventas/compras desde que se cargo la tabla. El conteo tipeado es el
        // total final: el movimiento es la diferencia contra ese valor actual,
        // sin importar si el stock estaba en positivo o en negativo.
        let current;
        this.isLoading = true;
        try {
          current = await this.currentStock(product, warehouse);
        } catch (error) {
          this.pushError(`No se pudo leer el stock actual de ${product.name}. No se guardó nada.`);
          return;
        } finally {
          this.isLoading = false;
        }

        const delta = Number((count - current).toFixed(4));
        if (delta === 0) {
          this.resetCount(product, warehouse);
          this.$store.commit('success/pushSuccess', `${product.name} ya estaba en ${count} unidades en ${warehouse.name}.`);
          return;
        }

        const sign = delta > 0 ? 'entrada' : 'salida';
        const ok = await this.$confirm(
          `El stock de ${product.name} en ${warehouse.name} pasará de ${current} a ${count} unidades. ` +
          `Se registrará una ${sign} de ${Math.abs(delta)} unidades.`,
          { title: 'Ajustar stock', okText: 'Ajustar', variant: 'danger' }
        );
        if (!ok) return;

        this.isLoading = true;
        try {
          const response = await this.$http.post("/api/movements/add", {
            product_id: product.id,
            quantity: Math.abs(delta),
            type: delta > 0 ? 'in' : 'out',
            destination_select: warehouse.id,
            reason: this.reason,
          });
          // El backend responde 200 con la clave mal escrita ("sucess") cuando falla,
          // asi que un error no se nota mirando solo el status HTTP.
          const body = response.data || {};
          if (body.sucess === false || body.success === false) {
            throw new Error(body.message || 'El backend rechazó el movimiento');
          }
          this.resetCount(product, warehouse);
          await this.getStock();
        } catch (error) {
          this.pushError(`No se pudo ajustar ${product.name}: ${error.message}`);
          this.isLoading = false;
        }
      },
    },
    computed: {
      brandFilterOptions() {
        return [{ value: 'Sin marca', label: 'Sin Marca' }, ...this.brands.map(brand => ({ value: brand.id, label: brand.name }))];
      },
      categoryFilterOptions() {
        return [{ value: 'Sin categoria', label: 'Sin Categoría' }, ...this.categories.map(category => ({ value: category.id, label: category.name }))];
      },
      providerFilterOptions() {
        return [{ value: 'Sin proveedor', label: 'Sin Proveedor' }, ...this.providers.map(provider => ({ value: provider.id, label: provider.name }))];
      },
    },
    watch: {
      'filters.qname': {handler: function () {if(this.suppressReload){return;} this.getStockWithDelay();}},
      'filters.qcode': {handler: function () {if(this.suppressReload){return;} this.getStockWithDelay();}},
      'filters.qbarcode': {handler: function () {if(this.suppressReload){return;} this.getStockWithDelay();}},
      'filters.qmanufacturercode': {handler: function () {if(this.suppressReload){return;} this.getStockWithDelay();}},
      'filters.brand_id': {handler: function () {if(this.suppressReload){return;} this.filters.page = 1; this.getStock();}},
      'filters.category_id': {handler: function () {if(this.suppressReload){return;} this.filters.page = 1; this.getStock();}},
      'filters.provider_id': {handler: function () {if(this.suppressReload){return;} this.filters.page = 1; this.getStock();}},
      'filters.active': {handler: function () {if(this.suppressReload){return;} this.filters.page = 1; this.getStock();}},
      'filters.negative_stock': {handler: function () {if(this.suppressReload){return;} this.filters.page = 1; this.getStock();}},
      'filters.page': {handler: function () {this.getStock();}},
      'filters.itemsPerPage': {handler: function () {this.getStock();}},
    },
  };
  </script>

  <style scoped>
  .button-close{
    background: transparent;
    border: none;
  }

  .stock-cell{
    text-align: center;
    vertical-align: middle;
  }

  .stock-cell__row{
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .stock-cell__current{
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.2;
    min-width: 1.5em;
    text-align: right;
  }

  .stock-cell__input{
    display: block;
    width: 64px;
    height: 32px;
    padding: 2px 8px;
    font-size: 13px;
    font-weight: 600;
    text-align: center;
    color: var(--text);
    background-color: var(--surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  }

  .stock-cell__input:focus{
    border-color: var(--brand-strong);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
    outline: none;
  }

  .stock-cell__input--pending{
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(242, 171, 39, 0.2);
  }

  .stock-cell__hint{
    margin-top: 6px;
    color: var(--accent);
    font-size: 11px;
    line-height: 1.3;
  }

  .table tbody tr:nth-of-type(odd) td:not(.stock-cell--negative){
    background-color: var(--surface-subtle);
  }

  .total-cell{
    font-weight: 600;
    text-align: center;
  }

  .empty-state{
    text-align: center;
    padding: 2rem 1rem;
    color: var(--text-muted);
  }

  .stock-cell--negative{
    background-color: rgba(220, 53, 69, 0.12);
    color: #a71d2a;
  }

  .texto-negro{
    color: black;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.5s;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
  </style>